import { z } from 'zod';

import type { RevenueSource } from '@/shared/api/types';
import { addDays, addMonthsToMonthStart, formatMonth, kstToday, monthEnd } from '@/shared/lib/date';

import type { RevenueSummaryRange } from './api';

/** 결제 내역 수익원 필터 — 전체 또는 한 수익원 */
export type SourceFilter = 'ALL' | RevenueSource;

export type SummaryPeriod = 'this-month' | 'last-month' | '3-months' | 'this-year';

/**
 * 요약 기간 선택지 — "이번 달"이 1일에는 지난달을 가리키므로(기준일 D-1) 달 이름으로 보여 준다.
 * 예) 9월 29일: 9월 · 8월 · 최근 3개월 · 2026년 / 10월 1일: 9월 · 8월 · …
 */
export function summaryPeriodOptions(
  today: string = kstToday(),
): { value: SummaryPeriod; label: string }[] {
  const base = addDays(today, -1);
  const lastMonth = addMonthsToMonthStart(base, -1);
  return [
    { value: 'this-month', label: `${Number(base.slice(5, 7))}월` },
    { value: 'last-month', label: `${Number(lastMonth.slice(5, 7))}월` },
    { value: '3-months', label: '최근 3개월' },
    { value: 'this-year', label: `${base.slice(0, 4)}년` },
  ];
}

/** 결제 내역에서 고를 수 있는 달 수 — 기준일이 속한 달부터 거꾸로 */
export const TRANSACTION_MONTH_COUNT = 12;

/**
 * 결제 내역 달 선택지 — 기준일(D-1)이 속한 달부터 최근 12개월, 최신이 먼저.
 * 1일에는 기준일이 지난달이므로 지난달부터 시작한다 (요약의 "이번 달"과 같은 규칙).
 */
export function transactionMonthOptions(
  today: string = kstToday(),
): { value: string; label: string }[] {
  const base = addDays(today, -1);
  return Array.from({ length: TRANSACTION_MONTH_COUNT }, (_, index) => {
    const month = addMonthsToMonthStart(base, -index).slice(0, 7);
    return { value: month, label: formatMonth(month) };
  });
}

/** 주소창의 달(YYYY-MM)을 선택지 안의 값으로 — 비었거나 범위 밖이면 가장 최근 달 */
export function resolveTransactionMonth(month: string | undefined, today: string = kstToday()) {
  const options = transactionMonthOptions(today);
  return options.find(option => option.value === month)?.value ?? options[0]?.value ?? '';
}

/**
 * 결제 내역 달 → API 조건. 가장 최근 달은 조건 없이 보내 서버가 그 달 1일 ~ 기준일로 잡게 하고,
 * 지난달들은 1일 ~ 말일을 보낸다.
 */
export function transactionRange(month: string, today: string = kstToday()): RevenueSummaryRange {
  if (month === resolveTransactionMonth(undefined, today)) return {};
  const from = `${month}-01`;
  return { from, to: monthEnd(from) };
}

export const revenueSearchSchema = z.object({
  period: z
    .enum(['this-month', 'last-month', '3-months', 'this-year'])
    .default('this-month')
    .catch('this-month'),
  source: z
    .enum(['ALL', 'GACHA', 'FEED_UNLOCK', 'CHAT_MESSAGE', 'PRODUCT'])
    .default('ALL')
    .catch('ALL'),
  /** 결제 내역 달 (YYYY-MM) — 비우면 가장 최근 달 */
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
});

export type RevenueSearch = z.infer<typeof revenueSearchSchema>;

/**
 * 요약 기간 → API 조건. 기준은 어제(D-1).
 * 이번 달은 조건 없이 보내 서버가 기준일이 속한 달로 잡게 한다 (1일에는 지난달이 된다).
 */
export function revenueSummaryRange(
  period: SummaryPeriod,
  today: string = kstToday(),
): RevenueSummaryRange {
  const base = addDays(today, -1);
  switch (period) {
    case 'this-month':
      return {};
    case 'last-month': {
      const from = addMonthsToMonthStart(base, -1);
      return { from, to: monthEnd(from) };
    }
    case '3-months':
      return { from: addMonthsToMonthStart(base, -2) };
    case 'this-year':
      return { from: `${base.slice(0, 4)}-01-01` };
  }
}
